import * as React from "react";
import { useCallback, useMemo } from "react";
import { styled } from "@mui/material";
import { LandingPageCard } from "../landingPageCard";
import { useUserStorage } from "fe-web-components/userStorage/useUserStorage";
import { UserStorageKey } from "fe-web-components/userStorage/userStorageKey";
import { PatientCaseWidget } from "../../patientChart/patientCaseWidget";
import { PatientCase } from "../../../../context/diagnosticAgent";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { useDiagnosticNavigation } from "../../useDiagnosticNavigation";
import { useOpenSavedCases } from "../../hooks/useOpenSavedCases";
import { MobileView } from "../../../../util/aKaunGeneralUtil";
import { useRouteHelper } from "../../useRouteHelper";
import { AgentRoute } from "../../util/agentRoutes";
import {
  getLastSessions,
  useSession,
} from "fe-web-components/userSession/userSession";
import { usePatientCase } from "../../usePatientCase";

//region [[ Styles ]]

const RestoreLastCaseView = styled(LandingPageCard)({
  width: "100%",
  "& .lower-section button": {
    textDecoration: "none",
    color: KahunColorMap.Default,
    fontSize: "13px",
  },
});

const CaseView = styled(PatientCaseWidget)(({ theme }) => ({
  background: KahunColorMap.PrimaryDim,
  display: "flex",
  alignItems: "center",
  marginTop: "13px",
  "&:hover": {
    background: KahunColorMap.PrimaryDim,
  },
  borderRadius: "20px",
  [theme.breakpoints.down(MobileView)]: {
    borderRadius: "10px",
    padding: "10px 16px",
    paddingRight: "30px",
    minHeight: "auto",
  },
  "& .left, &:hover .left": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexFlow: "column",
    [theme.breakpoints.down(MobileView)]: {
      marginRight: "30px",
    },
  },
  "& .right > div": {
    minHeight: "auto",
  },
  // clear al useless extra padding from the right qf text
  "& .right": {
    margin: 0,
    "& > *": {
      padding: 0,
    },
    "& .MuiListItemText-root": {
      margin: 0,
    },
  },
  "& .case-gender-icon-container": {
    width: "auto",
    height: "auto",
    textAlign: "center",
    "& .list-item-avatar-icon": {
      margin: 0,
      minWidth: "auto",
    },
    "& img": {
      height: "43px",
      marginTop: 0,
      [theme.breakpoints.down(MobileView)]: {
        height: "31px",
      },
    },
  },
  "& .age-presenter": {
    margin: 0,
  },
  "& .age-presenter-number, & .age-presenter-units , &:hover .age-presenter-units, &:hover .age-presenter-units": {
    fontSize: "14px",
    lineHeight: "16px",
    fontWeight: 500,
    color: KahunColorMap.White,
    [theme.breakpoints.down(MobileView)]: {
      fontSize: "10px",
      lineHeight: "12px",
    },
  },
  "& .age-presenter-number, &:hover .age-presenter-number": {
    fontWeight: 700,
  },
  "& .qf-text, & .qf-refinements-text": {
    fontSize: "13px",
    lineHeight: "16px",
    color: KahunColorMap.White,
    [theme.breakpoints.down(MobileView)]: {
      fontSize: "11px",
      lineHeight: "14px",
    },
  },
}));
//endregion [[ Styles ]]

//region [[ Props ]]

export interface RestoreLastCaseProps {}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

//region [[ Text ]]
const restoreCardTitle = "Open Your Last Case";
const goToSavedCases = "[linkText=]My saved cases >[/linkText]";
//endregion [[ Text ]]
export const RestoreLastCase = ({ ...props }: RestoreLastCaseProps) => {
  const userStorage = useUserStorage();

  const { goToMyCases } = useOpenSavedCases();

  const { navigateTo } = useRouteHelper();

  const { resetSession } = useSession();

  const { setPatientCase } = usePatientCase();

  const {
    startOverDialogProps: { isLocalRestored },
    setStartOverDialogProps,
    setInWizard,
  } = useDiagnosticNavigation();

  // Look in all last saved session for a valid case
  const lastCase: PatientCase = useMemo(() => {
    const lastSessions = getLastSessions();
    let session, lastValidCase: PatientCase;
    while (!lastValidCase && lastSessions.length) {
      session = lastSessions.pop();
      lastValidCase = userStorage.readFromStorage(
        UserStorageKey.PatientCase,
        session,
      );
      // workaround to avoid empty cases
      if (!lastValidCase?.age?.low && !lastValidCase?.age?.high)
        lastValidCase = null;
    }
    return lastValidCase;
  }, [userStorage]);

  const loadLastCase = useCallback(() => {
    evt({
      category: CATEGORY.WELCOME_BACK_PAGE,
      action: ACTION.OPEN_LAST_CASE,
    });
    setStartOverDialogProps({ open: false, isLocalRestored });
    setPatientCase(lastCase);
    setInWizard(false);
    // Support sessions not being defined (when running in editor for ex.)
    resetSession && resetSession();
    navigateTo({ route: AgentRoute.DD });
  }, [
    setStartOverDialogProps,
    setPatientCase,
    lastCase,
    isLocalRestored,
    navigateTo,
    resetSession,
    setInWizard,
  ]);

  const goToSavedCasesClicked = useCallback(() => {
    evt({
      category: CATEGORY.WELCOME_BACK_PAGE,
      action: ACTION.NAVIGATED_TO_SAVED_CASES,
    });
    goToMyCases();
  }, [goToMyCases]);

  if (!lastCase) return null;

  return (
    <RestoreLastCaseView
      title={restoreCardTitle}
      actionView={<CaseView ageUnderGender patientCase={lastCase} />}
      onClick={loadLastCase}
      bottomText={goToSavedCases}
      onBottomActionClicked={goToSavedCasesClicked}
    />
  );
};
